/**
 * Ecommerce Costs page — manage per-user catalog of cost components used to
 * compute true profit per campaign in the dashboard.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2, Plus, Loader2 } from "lucide-react";

type CostType =
  | "cogs_pct"
  | "cogs_fixed"
  | "shipping_pct"
  | "shipping_fixed"
  | "platform_fee_pct"
  | "monthly_fixed"
  | "one_time";

interface EcommerceCost {
  id: string;
  label: string;
  costType: CostType;
  value: number;
  currency: string;
  productId: string | null;
  campaignId: string | null;
  startDate: string | null;
  endDate: string | null;
  active: boolean;
  notes: string | null;
}

const COST_TYPE_OPTIONS: { value: CostType; label: string; isPct: boolean; description: string }[] =
  [
    {
      value: "cogs_pct",
      label: "COGS — % przychodu",
      isPct: true,
      description: "Koszt produktu jako procent ceny sprzedaży",
    },
    {
      value: "cogs_fixed",
      label: "COGS — kwota stała",
      isPct: false,
      description: "Stały koszt produktu na zamówienie",
    },
    {
      value: "shipping_pct",
      label: "Wysyłka — % przychodu",
      isPct: true,
      description: "Koszt wysyłki jako procent",
    },
    {
      value: "shipping_fixed",
      label: "Wysyłka — kwota stała",
      isPct: false,
      description: "Stały koszt wysyłki na zamówienie",
    },
    {
      value: "platform_fee_pct",
      label: "Prowizja platformy — %",
      isPct: true,
      description: "Shopify/Stripe/PayPal prowizje od transakcji",
    },
    {
      value: "monthly_fixed",
      label: "Koszt miesięczny",
      isPct: false,
      description: "Stały koszt miesięczny (czynsz, software, pensje)",
    },
    {
      value: "one_time",
      label: "Koszt jednorazowy",
      isPct: false,
      description: "Jednorazowy wydatek w wybranym okresie",
    },
  ];

function formatCostValue(c: EcommerceCost): string {
  const opt = COST_TYPE_OPTIONS.find((o) => o.value === c.costType);
  if (opt?.isPct) return `${(c.value * 100).toFixed(2)}%`;
  return `${c.value.toFixed(2)} ${c.currency}`;
}

/** VP-213: szybkie presety — wypełniają formularz typowym kosztem (wartość do korekty). */
const COST_PRESETS: { label: string; preset: Partial<EcommerceCost> }[] = [
  {
    label: "+ Kurier (zł/zam.)",
    preset: { costType: "shipping_fixed", label: "Kurier / wysyłka", value: 0 },
  },
  {
    label: "+ Procesor płatności (%)",
    preset: { costType: "platform_fee_pct", label: "Procesor płatności", value: 2 },
  },
  { label: "+ COGS (%)", preset: { costType: "cogs_pct", label: "COGS", value: 30 } },
];

export default function EcommerceCostsPage() {
  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <EcommerceCostsPanel />
    </div>
  );
}

export function EcommerceCostsPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<EcommerceCost> | null>(null);

  const { data, isLoading } = useQuery<{ costs: EcommerceCost[] }>({
    queryKey: ["/api/ecommerce-costs"],
    queryFn: () => apiRequest("GET", "/api/ecommerce-costs"),
  });

  // VP-213: katalog produktów Shopify do przypinania kosztów per produkt.
  // Pusta lista = brak integracji → formularz pokazuje ręczne pole (fallback).
  const { data: productsData } = useQuery<{ products: { id: string; title: string }[] }>({
    queryKey: ["/api/ecommerce-costs/products"],
    queryFn: () => apiRequest("GET", "/api/ecommerce-costs/products"),
    staleTime: 5 * 60_000,
  });
  const products = productsData?.products ?? [];
  const productTitle = (id: string | null) => products.find((p) => p.id === id)?.title;

  // Kolumny Net Profit/ROAS na dashboardzie liczą ze stawek /rates — bez tej
  // invalidacji zmiana kosztu nie docierałaby do tabeli przez staleTime 5 min
  // (klucz ["/api/ecommerce-costs"] NIE jest prefiksem ["/api/ecommerce-costs/rates"]).
  const invalidateCostCaches = () => {
    qc.invalidateQueries({ queryKey: ["/api/ecommerce-costs"] });
    qc.invalidateQueries({ queryKey: ["/api/ecommerce-costs/rates"] });
  };

  const createMut = useMutation({
    mutationFn: (payload: Partial<EcommerceCost>) =>
      apiRequest("POST", "/api/ecommerce-costs", payload),
    onSuccess: () => {
      invalidateCostCaches();
      toast({ title: "Dodano koszt" });
      setEditing(null);
    },
    onError: (e: Error) =>
      toast({ title: "Nie udało się dodać", description: e.message, variant: "destructive" }),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<EcommerceCost> }) =>
      apiRequest("PATCH", `/api/ecommerce-costs/${id}`, payload),
    onSuccess: () => {
      invalidateCostCaches();
      toast({ title: "Zapisano zmiany" });
      setEditing(null);
    },
    onError: (e: Error) =>
      toast({ title: "Nie udało się zapisać", description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/ecommerce-costs/${id}`),
    onSuccess: () => {
      invalidateCostCaches();
      toast({ title: "Usunięto koszt" });
    },
  });

  // Formularz operuje na PEŁNYCH procentach (30 = 30%); w DB trzymamy ułamek.
  // Stara heurystyka `value > 1 ? /100` zamieniała wpis '1' (1%) w 100% prowizji.
  const openEdit = (c: EcommerceCost) => {
    const opt = COST_TYPE_OPTIONS.find((o) => o.value === c.costType);
    setEditing(opt?.isPct ? { ...c, value: Math.round(c.value * 10000) / 100 } : c);
  };

  const handleSave = () => {
    if (!editing) return;
    if (!editing.label || !editing.costType || editing.value == null) {
      toast({ title: "Uzupełnij nazwę, typ i wartość", variant: "destructive" });
      return;
    }
    const opt = COST_TYPE_OPTIONS.find((o) => o.value === editing.costType);
    const value = opt?.isPct ? editing.value / 100 : editing.value;
    const payload = { ...editing, value };
    if (editing.id) patchMut.mutate({ id: editing.id, payload });
    else createMut.mutate(payload);
  };

  const costs = data?.costs ?? [];
  const editingOpt = editing && COST_TYPE_OPTIONS.find((o) => o.value === editing.costType);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white font-semibold text-base mb-1">Koszty e-commerce</h2>
          <p className="text-sm text-muted-foreground">
            Definiuj COGS, wysyłkę, prowizje i koszty stałe — używamy ich do liczenia prawdziwego
            zysku z każdej kampanii.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {COST_PRESETS.map((p) => (
            <Button
              key={p.label}
              variant="outline"
              size="sm"
              onClick={() => setEditing({ currency: "PLN", active: true, ...p.preset })}
            >
              {p.label}
            </Button>
          ))}
          <Button
            onClick={() =>
              setEditing({ costType: "cogs_pct", currency: "PLN", value: 0, active: true })
            }
          >
            <Plus className="w-4 h-4 mr-1" /> Dodaj koszt
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2">Nazwa</th>
                <th className="text-left px-4 py-2">Typ</th>
                <th className="text-right px-4 py-2">Wartość</th>
                <th className="text-left px-4 py-2">Zakres</th>
                <th className="text-right px-4 py-2">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-2">
                    <Skeleton className="h-4 w-3/4" />
                  </td>
                  <td className="px-4 py-2">
                    <Skeleton className="h-4 w-2/3" />
                  </td>
                  <td className="px-4 py-2">
                    <Skeleton className="h-4 w-16 ml-auto" />
                  </td>
                  <td className="px-4 py-2">
                    <Skeleton className="h-4 w-1/2" />
                  </td>
                  <td className="px-4 py-2">
                    <Skeleton className="h-4 w-20 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : costs.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground">
          Brak zdefiniowanych kosztów. Kliknij "Dodaj koszt" aby zacząć.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2">Nazwa</th>
                <th className="text-left px-4 py-2">Typ</th>
                <th className="text-right px-4 py-2">Wartość</th>
                <th className="text-left px-4 py-2">Zakres</th>
                <th className="text-right px-4 py-2">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {costs.map((c) => {
                const opt = COST_TYPE_OPTIONS.find((o) => o.value === c.costType);
                return (
                  <tr key={c.id} className={`border-t ${c.active ? "" : "opacity-50"}`}>
                    <td className="px-4 py-2 font-medium">{c.label}</td>
                    <td className="px-4 py-2 text-muted-foreground">{opt?.label ?? c.costType}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatCostValue(c)}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {c.campaignId
                        ? `Kampania: ${c.campaignId.slice(0, 12)}…`
                        : c.productId
                          ? `Produkt: ${productTitle(c.productId) ?? `${c.productId.slice(0, 12)}…`}`
                          : "Globalnie"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
                        Edytuj
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Usunąć "${c.label}"?`)) deleteMut.mutate(c.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing.id ? "Edytuj koszt" : "Nowy koszt"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Nazwa</Label>
                <Input
                  value={editing.label ?? ""}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  placeholder="np. COGS – linia podstawowa"
                />
              </div>
              <div>
                <Label>Typ kosztu</Label>
                <Select
                  value={editing.costType}
                  onValueChange={(v) => setEditing({ ...editing, costType: v as CostType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COST_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingOpt && (
                  <p className="text-xs text-muted-foreground mt-1">{editingOpt.description}</p>
                )}
              </div>
              <div>
                <Label>
                  {editingOpt?.isPct ? "Wartość (%)" : `Wartość (${editing.currency ?? "PLN"})`}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editing.value ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, value: parseFloat(e.target.value) || 0 })
                  }
                  placeholder={editingOpt?.isPct ? "np. 30 dla 30%" : "np. 9.99"}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Produkt (opc.)</Label>
                  {/* VP-213: wybór z katalogu sklepu zamiast ręcznego ID; koszt
                      przypięty do produktu liczy się od jego realnej sprzedaży. */}
                  {products.length > 0 ? (
                    <Select
                      value={editing.productId ?? "__none__"}
                      onValueChange={(v) =>
                        setEditing({ ...editing, productId: v === "__none__" ? null : v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="— cały sklep —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— cały sklep —</SelectItem>
                        {/* Pinned productId spoza katalogu (usunięty produkt / >2000 poz.)
                            musi być widoczny i możliwy do zachowania. */}
                        {editing.productId && !products.some((p) => p.id === editing.productId) && (
                          <SelectItem
                            value={editing.productId}
                          >{`ID: ${editing.productId} (spoza katalogu)`}</SelectItem>
                        )}
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={editing.productId ?? ""}
                      onChange={(e) =>
                        setEditing({ ...editing, productId: e.target.value || null })
                      }
                      placeholder="Shopify product ID (brak integracji)"
                    />
                  )}
                </div>
                <div>
                  <Label>ID kampanii (opc.)</Label>
                  <Input
                    value={editing.campaignId ?? ""}
                    onChange={(e) => setEditing({ ...editing, campaignId: e.target.value || null })}
                    placeholder="Meta/Google campaign ID"
                  />
                </div>
              </div>
              <div>
                <Label>Notatki</Label>
                <Input
                  value={editing.notes ?? ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value || null })}
                  placeholder="opcjonalnie"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>
                Anuluj
              </Button>
              <Button onClick={handleSave} disabled={createMut.isPending || patchMut.isPending}>
                {(createMut.isPending || patchMut.isPending) && (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                )}
                Zapisz
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
